import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock, LockKeyhole, MapPin, QrCode } from 'lucide-react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Calendar } from './ui/calendar';
import { listCalendarEvents } from '../lib/api';
import { normalizeSubmission } from './ApprovedEventsFeed';

const parseEventDate = (value) => value ? new Date(value) : null;
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const PublicEventsCalendar = ({ onSelect }) => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [month, setMonth] = useState(new Date());
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    listCalendarEvents().then((data) => {
      const active = data.filter((event) => !event.isArchived && parseEventDate(event.dateTime));
      setEvents(active);
      if (active[0]) {
        const firstDate = parseEventDate(active[0].dateTime);
        setSelectedDate(firstDate);
        setMonth(firstDate);
      }
      setStatus('ready');
    }).catch(() => setStatus('error'));
  }, []);

  const eventDates = useMemo(() => events.map((event) => parseEventDate(event.dateTime)), [events]);
  const selectedEvents = events.filter((event) => sameDay(parseEventDate(event.dateTime), selectedDate));
  const shownEvents = selectedEvents.length ? selectedEvents : events.slice(0, 4);
  const calendarUrl = typeof window !== 'undefined' ? `${window.location.origin}/events#calendar` : '/events#calendar';

  return (
    <section id="calendar" className="py-24 bg-[#f5f6f8] border-b border-[#0a1f44]/10" data-testid="public-events-calendar">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Public Schedule</div>
            <h2 className="font-display text-[#0a1f44] text-4xl md:text-5xl">EVENTS CALENDAR</h2>
            <p className="text-[#0a1f44]/70 mt-3">View upcoming Patriots events. Past events leave this calendar automatically after 24 hours.</p>
          </div>
          <Link to="/admin/login?next=/admin/events" data-testid="calendar-board-login" className="inline-flex items-center gap-2 self-start border-2 border-[#0a1f44] text-[#0a1f44] hover:bg-[#0a1f44] hover:text-white px-5 py-3 font-heading uppercase tracking-widest text-xs transition-colors">
            <LockKeyhole className="w-4 h-4" /> Board Calendar Login
          </Link>
        </div>

        <div className="grid lg:grid-cols-[1fr_0.85fr_0.55fr] border border-[#0a1f44]/15 bg-white">
          <div className="p-5 md:p-8 border-b lg:border-b-0 lg:border-r border-[#0a1f44]/15 min-w-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={month}
              onMonthChange={setMonth}
              modifiers={{ eventDay: eventDates }}
              modifiersClassNames={{ eventDay: 'after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-[#c8102e]' }}
              className="w-full p-0"
              classNames={{
                months: 'w-full', month: 'w-full space-y-5', table: 'w-full border-collapse',
                head_row: 'grid grid-cols-7', head_cell: 'text-[#0a1f44]/50 font-heading uppercase text-[10px] text-center',
                row: 'grid grid-cols-7 mt-2', cell: 'relative text-center p-0',
                day: 'relative h-11 sm:h-16 w-full border border-transparent hover:border-[#c8102e] font-heading transition-colors',
                day_selected: 'bg-[#0a1f44] text-white hover:bg-[#0a1f44]', day_today: 'border-[#c8102e] text-[#c8102e]',
                day_outside: 'text-[#0a1f44]/25', nav_button: 'h-8 w-8 border border-[#0a1f44]/20 hover:border-[#c8102e] p-0',
                nav_button_previous: 'absolute left-0', nav_button_next: 'absolute right-0', caption: 'flex justify-center relative items-center h-9',
                caption_label: 'font-display text-2xl text-[#0a1f44] uppercase',
              }}
            />
          </div>

          <div className="p-5 md:p-8 border-b lg:border-b-0 lg:border-r border-[#0a1f44]/15" data-testid="calendar-selected-events">
            <div className="font-heading uppercase tracking-[0.25em] text-[#c8102e] text-[10px] mb-5">
              {selectedEvents.length ? selectedDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Next Upcoming Events'}
            </div>
            {status === 'loading' && <div className="h-28 bg-[#0a1f44]/5 animate-pulse" data-testid="calendar-loading" />}
            {status === 'error' && <p className="text-sm text-[#c8102e]" data-testid="calendar-error">Calendar unavailable. Please try again shortly.</p>}
            {status === 'ready' && shownEvents.length === 0 && <p className="text-sm text-[#0a1f44]/60" data-testid="calendar-empty">No upcoming events have been added yet.</p>}
            <div className="space-y-3">
              {shownEvents.map((event) => {
                const normalized = normalizeSubmission(event);
                return (
                  <button key={event.id} type="button" onClick={() => onSelect(normalized)} data-testid={`calendar-event-${event.id}`} className="w-full text-left border-l-4 border-[#c8102e] bg-[#0a1f44]/[0.03] hover:bg-[#0a1f44] hover:text-white p-4 transition-colors group">
                    <div className="font-display text-xl leading-tight">{event.title.toUpperCase()}</div>
                    <div className="mt-2 space-y-1 text-xs opacity-70">
                      <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#c8102e]" /> {normalized.day}, {normalized.month} {normalized.date} · {normalized.time}</div>
                      <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#c8102e]" /> {normalized.location}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <aside className="hidden md:flex p-6 md:p-8 flex-col items-center justify-center text-center" data-testid="calendar-qr-panel">
            <QrCode className="w-7 h-7 text-[#c8102e] mb-3" />
            <div className="font-display text-[#0a1f44] text-2xl">SCAN THE CALENDAR</div>
            <div className="bg-white border-2 border-[#0a1f44] p-3 mt-5">
              <QRCodeCanvas value={calendarUrl} size={170} bgColor="#ffffff" fgColor="#0a1f44" level="M" data-testid="calendar-qr-code" />
            </div>
            <p className="text-xs text-[#0a1f44]/60 mt-4">Opens this public, read-only calendar on any phone.</p>
          </aside>
        </div>
      </div>
    </section>
  );
};